import React, { useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon, ArrowTrendingUpIcon, LightBulbIcon } from '@heroicons/react/24/outline';

interface ImprovementArea {
  area: string;
  count: number;
}

interface ImprovementAreasRankingProps {
  improvementAreas: ImprovementArea[];
  totalFeedbacks: number;
  className?: string;
}

export const ImprovementAreasRanking: React.FC<ImprovementAreasRankingProps> = ({ 
  improvementAreas, 
  totalFeedbacks,
  className = '' 
}) => {
  const [sortBy, setSortBy] = useState<'count' | 'percentage'>('count');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Preparar dados com percentuais
  const enrichedAreas = improvementAreas.map(area => ({
    ...area,
    percentage: totalFeedbacks > 0 ? (area.count / totalFeedbacks) * 100 : 0,
    priority: area.count >= Math.ceil(totalFeedbacks * 0.2) ? 'high' : 
              area.count >= Math.ceil(totalFeedbacks * 0.1) ? 'medium' : 'low'
  }));

  // Ordenar dados
  const sortedAreas = [...enrichedAreas].sort((a, b) => {
    const aValue = sortBy === 'count' ? a.count : a.percentage;
    const bValue = sortBy === 'count' ? b.count : b.percentage;
    return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
  });

  const handleSort = (newSortBy: 'count' | 'percentage') => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };

  const toggleExpanded = (area: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(area)) {
      newExpanded.delete(area);
    } else {
      newExpanded.add(area);
    }
    setExpandedItems(newExpanded);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high': return 'Alta Prioridade';
      case 'medium': return 'Média Prioridade';
      case 'low': return 'Baixa Prioridade';
      default: return 'Prioridade Baixa';
    }
  };

  const getActionSuggestion = (area: string, count: number) => {
    const suggestions: Record<string, string> = {
      'Mais exemplos práticos': 'Considere adicionar mais casos de uso reais e exemplos aplicáveis ao dia a dia empresarial.',
      'Conteúdo mais personalizado': 'Implemente sistema de personalização baseado no perfil e setor de atuação do usuário.',
      'Estratégias mais específicas': 'Desenvolva estratégias segmentadas por área de negócio e situações específicas.',
      'Recursos adicionais': 'Crie biblioteca de recursos complementares: templates, checklists, e ferramentas práticas.',
      'Interface mais intuitiva': 'Realize testes de usabilidade e simplifique a navegação baseado no feedback dos usuários.',
      'Explicações mais claras': 'Revise o conteúdo com foco em linguagem mais acessível e estrutura mais didática.',
      'Plano de ação mais detalhado': 'Expanda os planos com passos mais específicos, timelines e métricas de sucesso.',
      'Feedback mais visual': 'Implemente mais gráficos, infográficos e elementos visuais para melhor compreensão.',
    };
    
    return suggestions[area] || 'Analise esta área específica para entender as necessidades dos usuários e desenvolver melhorias direcionadas.';
  };

  if (sortedAreas.length === 0) {
    return (
      <div className={`flex items-center justify-center h-64 bg-gray-50 rounded-lg ${className}`}>
        <div className="text-center">
          <LightBulbIcon className="w-12 h-12 mx-auto text-gray-400 mb-2" />
          <p className="text-gray-500">Nenhuma sugestão de melhoria disponível</p>
          <p className="text-sm text-gray-400 mt-1">Aguardando feedback dos usuários</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <ArrowTrendingUpIcon className="w-5 h-5 mr-2 text-blue-600" />
              Áreas de Melhoria Mais Solicitadas
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Baseado em {totalFeedbacks} feedbacks coletados
            </p>
          </div>
          
          {/* Controles de ordenação */}
          <div className="flex gap-2">
            <button
              onClick={() => handleSort('count')}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                sortBy === 'count'
                  ? 'bg-blue-100 text-blue-800 border-blue-200'
                  : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
              }`}
            >
              Por Contagem
              {sortBy === 'count' && (
                sortOrder === 'desc' ? 
                <ChevronDownIcon className="w-3 h-3 inline ml-1" /> : 
                <ChevronUpIcon className="w-3 h-3 inline ml-1" />
              )}
            </button>
            <button
              onClick={() => handleSort('percentage')}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                sortBy === 'percentage'
                  ? 'bg-blue-100 text-blue-800 border-blue-200'
                  : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
              }`}
            >
              Por %
              {sortBy === 'percentage' && (
                sortOrder === 'desc' ? 
                <ChevronDownIcon className="w-3 h-3 inline ml-1" /> : 
                <ChevronUpIcon className="w-3 h-3 inline ml-1" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Ranking List */}
      <div className="divide-y divide-gray-200">
        {sortedAreas.map((item, index) => (
          <div key={item.area} className="p-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1">
                {/* Ranking Position */}
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                  index === 0 ? 'bg-yellow-100 text-yellow-800' :
                  index === 1 ? 'bg-gray-100 text-gray-800' :
                  index === 2 ? 'bg-orange-100 text-orange-800' :
                  'bg-gray-50 text-gray-600'
                }`}>
                  {index + 1}
                </div>

                {/* Area Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900">{item.area}</h4>
                    <span className={`px-2 py-1 text-xs rounded-full border ${getPriorityColor(item.priority)}`}>
                      {getPriorityLabel(item.priority)}
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-2 flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-1000 ease-out"
                        style={{ width: `${Math.min(item.percentage, 100)}%` }}
                      />
                    </div>
                    <div className="text-sm text-gray-600 min-w-fit">
                      {item.count} ({item.percentage.toFixed(1)}%)
                    </div>
                  </div>
                </div>

                {/* Expand Button */}
                <button
                  onClick={() => toggleExpanded(item.area)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {expandedItems.has(item.area) ? 
                    <ChevronUpIcon className="w-5 h-5" /> : 
                    <ChevronDownIcon className="w-5 h-5" />
                  }
                </button>
              </div>
            </div>

            {/* Expanded Content */}
            {expandedItems.has(item.area) && (
              <div className="mt-4 ml-11 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                <h5 className="font-medium text-blue-900 mb-2">💡 Sugestão de Ação:</h5>
                <p className="text-sm text-blue-800 leading-relaxed">
                  {getActionSuggestion(item.area, item.count)}
                </p>
                <div className="mt-3 text-xs text-blue-700">
                  <span className="font-medium">Impacto estimado:</span> {
                    item.priority === 'high' ? 'Alto - Pode afetar significativamente a satisfação dos usuários' :
                    item.priority === 'medium' ? 'Médio - Melhoria importante para experiência do usuário' :
                    'Baixo - Melhoria incremental para usuários específicos'
                  }
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary Footer */}
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4 text-center text-sm">
          <div>
            <div className="font-medium text-gray-900">{sortedAreas.length}</div>
            <div className="text-gray-500">Áreas Identificadas</div>
          </div>
          <div>
            <div className="font-medium text-red-600">
              {sortedAreas.filter(a => a.priority === 'high').length}
            </div>
            <div className="text-gray-500">Alta Prioridade</div>
          </div>
          <div>
            <div className="font-medium text-blue-600">
              {sortedAreas.length > 0 ? (sortedAreas.reduce((sum, area) => sum + area.count, 0) / sortedAreas.length).toFixed(1) : '0'}
            </div>
            <div className="text-gray-500">Menções Médias</div>
          </div>
        </div>
      </div>
    </div>
  );
};